/* shared/state — centralized CRM store (Phase 7B: fully wired).
   Loads from backend using activeWorkspaceId from session.
   All mutations are optimistic: local state updates immediately, backend
   sync runs in the background. On sync failure a warning toast is shown
   but local state is kept (data survives the session).

   Phase 7B additions vs 7A:
   - M4: Pipeline stage maps (_stageShortMap / _stageUuidMap) loaded per workspace;
         normDealWithStage translates UUID ↔ s1-s6 at the boundary.
   - M5: update('tasks', id, {done}) intercepted → taskApi.complete / reopen (POST).
   - M8: saveView / removeView wired to savedViewApi; views loaded from backend.
   - M3: notes[] in state; createNote / updateNote / removeNote backed by noteApi.
*/
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { applyBrandingTokens, clearBrandingTokens, setLastWorkspaceBranding } from '../branding/index.js';
import { useToast } from '../feedback';
import * as peopleApi    from '../../api/people.api.js';
import * as companyApi   from '../../api/company.api.js';
import * as dealApi      from '../../api/deal.api.js';
import * as taskApi      from '../../api/task.api.js';
import * as documentApi  from '../../api/document.api.js';
import * as noteApi      from '../../api/note.api.js';
import * as savedViewApi  from '../../api/savedview.api.js';
import * as workspaceApi  from '../../api/workspace.api.js';
import * as activityApi   from '../../api/activity.api.js';
import * as brandingApi      from '../../api/branding.api.js';
import * as customFieldsApi  from '../../api/crm-custom-fields.api.js';
import * as stageApi         from '../../api/stage.api.js';
import {
  normPerson, normCompany, normDeal, normTask, normDocument,
  LOAN_TYPE, EMPLOYMENT_TYPE, REPAYMENT_STATUS, RISK_LEVEL, TASK_PRIORITY,
  COMPANY_LIFECYCLE, PERSON_STATUS,
} from '../../api/enums.js';

const StoreCtx = createContext(null);

/* Normalize legacy product name strings that were saved before the URSA rename. */
const LEGACY_BRAND_NAMES = new Set(['Nexus CRM', 'Nexus Platform', 'Nexus', 'NEXUS', 'nexus crm']);
const normalizeBranding = (b) => {
  if (!b) return b;
  const name = b.brandName;
  if (name && LEGACY_BRAND_NAMES.has(name)) return { ...b, brandName: 'URSA' };
  return b;
};

/* Apply resolved branding CSS variables and favicon to the document root.
   Delegates CSS custom-property injection to the canonical applyBrandingTokens
   so all derived accent tokens (hover, active, tint-2) are always set together.
   Guards: skips CSS application on /platform/* (BUG-003) and /organization/* (BRANDING-006).
   setLastWorkspaceBranding is always called (before the guards) so OrganizationLayout's
   exit cleanup can restore workspace branding even when CSS was not applied here.
   Exported so WorkspaceShell can re-apply cached store.branding on remount (BUG-D). */
export const applyBrandingGlobally = (b) => {
  if (!b || typeof document === 'undefined') return;
  setLastWorkspaceBranding(b); // always cache — OrgLayout exit cleanup reads this
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/platform')) return;
  /* BRANDING-006: StoreProvider.loadWorkspace runs on all routes (StoreProvider wraps both
     CRM and Org Console under the same React tree). Without this guard, loadWorkspace's async
     applyBrandingGlobally(resolvedWorkspaceBranding) overwrites OrganizationLayout's org-scoped
     CSS vars after they are applied, causing the shell to revert to workspace branding (green). */
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/organization')) return;
  /* CRM-WORKSPACE-002 BUG-1: WorkspaceConsoleLayout applies workspace-specific WL directly via
     applyBrandingTokens. Without this guard, StoreProvider's loadWorkspace (which runs on all
     routes) applies resolved/org branding over it, causing green org branding to bleed into
     the Workspace Admin shell even when no workspace WL is configured. */
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/workspaces')) return;
  applyBrandingTokens(b);
  if (b.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = b.faviconUrl;
  }
  if (b.brandName) document.title = b.brandName;
};

/* M4: Pipeline stage maps — module-level so loadWorkspace can mutate them */
let _stageShortMap    = {}; // { [uuid]: 's1' }
let _stageUuidMap     = {}; // { 's1': uuid }
let _activePipelineId = null; // workspace-scoped active pipeline; null = default

/* Member name→userId map — populated on workspace load for owner/assignee UUID resolution */
let _memberMap = {}; // { [memberName]: userId }

/* ── Empty initial state ─────────────────────────────────── */
const emptyState = () => ({
  people:           [],
  companies:        [],
  deals:            [],
  archivedDeals:    [],
  tasks:            [],
  archivedTasks:    [],
  notes:            [],
  activity:         [],
  conversations:    [],
  documents:        [],
  pipeline:         [],
  pipelines:        [],
  allStages:        [],
  savedViews:       [],
  memberNames:      [],
  workspaceMembers: [],
  branding:         null,
  modules:          [],
  customFieldDefs:  { people: [], companies: [], deals: [], tasks: [] },
  cfValues:         { people: {}, companies: {}, deals: {}, tasks: {} },
  cfColumns:        { people: null, companies: null, deals: null, tasks: null },
  cfColumnsDefaults: { people: null, companies: null, deals: null, tasks: null },
  currentMemberId:  null, // LV-05: Member.id of the logged-in user; null for SA
});

/* ── Payload builders: frontend shape → backend body ─────── */
const toPersonPayload = (p) => {
  const out = { name: p.name };
  if (p.salutation !== undefined) out.salutation = p.salutation || null;
  if (p.email)               out.email           = p.email;
  if (p.phone)               out.phone           = p.phone;
  if (p.role)                out.role            = p.role;
  if (p.companyId)           out.companyId       = p.companyId;
  if (p.company)             out.company         = p.company;
  if (p.score  != null)      out.score           = p.score;
  if (p.status)              out.status          = PERSON_STATUS.toBe[p.status] || p.status;
  if (p.owner)               out.owner           = p.owner;
  if (p.city)                out.city            = p.city;
  if (p.linkedin)            out.linkedin        = p.linkedin;
  if (p.tags !== undefined)  out.tags            = p.tags;
  if (p.creditScore != null) out.creditScore     = p.creditScore;
  if (p.loanType)            out.loanType        = LOAN_TYPE.toBe[p.loanType]             || undefined;
  if (p.employmentType)      out.employmentType  = EMPLOYMENT_TYPE.toBe[p.employmentType] || undefined;
  if (p.repaymentStatus)     out.repaymentStatus = REPAYMENT_STATUS.toBe[p.repaymentStatus] || undefined;
  if (p.riskLevel)           out.riskLevel       = RISK_LEVEL.toBe[p.riskLevel]           || undefined;
  if (p.income != null)      out.income          = p.income;
  return out;
};

const toCompanyPayload = (c) => {
  const out = { name: c.name };
  if (c.domain)                     out.domain          = c.domain;
  if (c.industry)                   out.industry        = c.industry;
  if (c.employees != null)          out.employees       = c.employees;
  if (c.revenue)                    out.revenue         = c.revenue;
  if (c.location)                   out.location        = c.location;
  if (c.lifecycle)                  out.lifecycle       = COMPANY_LIFECYCLE.toBe[c.lifecycle] || c.lifecycle;
  if (c.score != null)              out.score           = c.score;
  if (c.owner)                      out.owner           = c.owner;
  if (c.tags !== undefined)         out.tags            = c.tags;
  if (c.parentCompanyId !== undefined) out.parentCompanyId = c.parentCompanyId ?? null;
  return out;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const toDealPayload = (d) => {
  const out = { title: d.title };
  if (d.value != null)       out.value       = d.value;
  if (d.currency)            out.currency    = d.currency;
  if (d.stageId) {
    // FIX-04: only include stageId if it resolves to a valid UUID — never send short IDs to backend
    const uuid = _stageUuidMap[d.stageId];
    if (uuid) out.stageId = uuid;
    else if (UUID_RE.test(d.stageId)) out.stageId = d.stageId;
    // else: unresolvable short ID (pipeline not loaded) → omit; caught by apiCreate guard
  }
  if (d.companyId)           out.companyId   = d.companyId;
  if (d.contactId)           out.personId    = d.contactId;
  if (d.probability != null) out.probability = d.probability;
  if (d.closeDate)           out.expectedCloseDate = d.closeDate;
  if (d.tags?.length)        out.tags        = d.tags;
  if (d.priority)            out.priority    = TASK_PRIORITY.toBe[d.priority] || d.priority;
  // Resolve owner name → User UUID using workspace member map
  if (d.owner && _memberMap[d.owner]) out.ownerId = _memberMap[d.owner];
  else if (d.ownerId)                 out.ownerId = d.ownerId;
  return out;
};

const toTaskPayload = (t) => {
  const out = { title: t.title };
  if (t.dueDate)    out.dueDate     = t.dueDate;
  if (t.priority)   out.priority    = TASK_PRIORITY.toBe[t.priority] || t.priority;
  if (t.notes !== undefined) out.description = t.notes || '';
  if (t.personId  !== undefined) out.personId  = t.personId  ?? null;
  if (t.companyId !== undefined) out.companyId = t.companyId ?? null;
  if (t.dealId    !== undefined) out.dealId    = t.dealId    ?? null;
  // Prefer explicit UUID; fall back to name→UUID lookup for form-created tasks
  if (t.assigneeId)                       out.assigneeId = t.assigneeId;
  else if (t.assignee && _memberMap[t.assignee]) out.assigneeId = _memberMap[t.assignee];
  return out;
};

const apiCreate = (type, wsId, payload) => {
  switch (type) {
    case 'people':    return peopleApi.create(wsId,  toPersonPayload(payload));
    case 'companies': return companyApi.create(wsId, toCompanyPayload(payload));
    case 'deals': {
      // FIX-04: pre-flight — toDealPayload omits unresolvable short IDs; reject before hitting network
      const dealPayload = toDealPayload(payload);
      if (!dealPayload.stageId) return Promise.reject(new Error('Stage selection is required. Please wait for the pipeline to load and try again.'));
      return dealApi.create(wsId, dealPayload);
    }
    case 'tasks':     return taskApi.create(wsId,    toTaskPayload(payload));
    default: return Promise.reject(new Error('No API for type: ' + type));
  }
};

const apiUpdate = (type, wsId, id, patch) => {
  switch (type) {
    case 'people':    return peopleApi.update(wsId,  id, toPersonPayload(patch));
    case 'companies': return companyApi.update(wsId, id, toCompanyPayload(patch));
    case 'deals':     return dealApi.update(wsId,    id, toDealPayload(patch));
    case 'tasks':     return taskApi.update(wsId,    id, toTaskPayload(patch));
    default: return Promise.resolve(null);
  }
};

const apiRemove = (type, wsId, id) => {
  switch (type) {
    case 'people':    return peopleApi.remove(wsId,  id);
    case 'companies': return companyApi.remove(wsId, id);
    case 'deals':     return dealApi.remove(wsId,    id);
    case 'tasks':     return taskApi.remove(wsId,    id);
    default: return Promise.resolve(null);
  }
};

const normRecord = (type, r) => {
  switch (type) {
    case 'people':    return normPerson(r);
    case 'companies': return normCompany(r);
    case 'deals': {
      const d = normDeal(r);
      if (d.stageId && _stageShortMap[d.stageId]) d.stageId = _stageShortMap[d.stageId];
      return d;
    }
    case 'tasks':     return normTask(r);
    case 'documents': return normDocument(r);
    default: return r;
  }
};

/* Backend Note → frontend note shape (delegates to noteApi.normNote for relative time) */
const normNoteLocal = (n) => noteApi.normNote(n);

/* ── Label helpers ─────────────────────────────────────────── */
const titleOf = (type, verb) =>
  ({ people: 'Person', companies: 'Company', deals: 'Deal', tasks: 'Task' }[type] || 'Record') + ' ' + verb;
const singular = (type) =>
  ({ people: 'person', companies: 'company', deals: 'deal', tasks: 'task' }[type] || 'record');

/* ── StoreProvider ─────────────────────────────────────────── */
export const StoreProvider = ({ children }) => {
  const toast = useToast();
  const [state, setState] = useState(emptyState);
  const [wsId, setWsId]   = useState(() => window.__nx_auth?.user?.activeWorkspaceId || null);
  const [loading,   setLoading]   = useState(false);
  const [loadError, setLoadError] = useState(false);

  /* Sync ref so callbacks can read latest state without capturing it in deps */
  const stateRef = useRef(null);
  stateRef.current = state;

  /* Track wsId across the stale nx-session-changed closure so we can detect workspace changes */
  const wsIdRef = useRef(wsId);
  wsIdRef.current = wsId;

  const [drawer,       setDrawer]       = useState(null);
  const [createForm,   setCreateForm]   = useState(null);
  const [editForm,     setEditForm]     = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  /* Track current drawer ID so remove() can auto-close if the active record is deleted */
  const drawerIdRef = useRef(null);
  useEffect(() => { drawerIdRef.current = drawer?.id ?? null; }, [drawer]);

  /* Track pending temp-ID → real-ID replacements */
  const pendingIds = useRef({});

  /* WS-002: unique token per loadWorkspace call — stale calls discard their results */
  const loadIdRef = useRef(null);

  /* RT-02: track the previously loaded workspace so we can distinguish
     a browser reload (same workspace → preserve sessionStorage) from
     an actual workspace switch (different workspace → clear sessionStorage). */
  const prevLoadedWsIdRef = useRef(null);

  /* ── Load CRM data for the active workspace ──────────────── */
  const loadWorkspace = useCallback(async (id) => {
    if (!id) return;

    /* WS-002: stamp this invocation so stale responses from prior workspace can be discarded */
    const loadToken = {};
    loadIdRef.current = loadToken;

    /* WS-001: reset stage maps before each load so stale UUIDs from prev workspace don't linger */
    _stageShortMap = {};
    _stageUuidMap  = {};
    _memberMap     = {};

    /* Clear stale data from any previous workspace before loading the new one.
       Prevents cross-workspace data leaks during the async fetch window. */
    setState(emptyState());
    /* INC-12: clear ALL branding CSS vars so prior workspace colors don't bleed
       into the new workspace during the async resolved-branding fetch.
       Previously only 6 of 13 vars were reset — accent-hover, accent-active,
       accent-tint-2, *-tint variants, and font would bleed until the fetch resolved. */
    clearBrandingTokens();
    document.title = window.__nx_auth?.user?.activeOrgName || window.__nx_auth?.user?.activeWorkspaceName || 'CRM';

    /* RT-02: Only clear list-state sessionStorage on an actual workspace switch.
       On browser reload prevLoadedWsIdRef is null (initial mount) or equals id
       (same workspace) — preserving search/filter/sort so useFilters can hydrate.
       On a real switch the ids differ → clear the previous workspace's state. */
    const isWorkspaceSwitch = prevLoadedWsIdRef.current !== null && prevLoadedWsIdRef.current !== id;
    prevLoadedWsIdRef.current = id;
    if (isWorkspaceSwitch) {
      try {
        ['people', 'companies', 'deals', 'tasks'].forEach(m => {
          sessionStorage.removeItem('nx-filters-' + m);
          sessionStorage.removeItem('nx-filters-' + m + '-search');
          sessionStorage.removeItem('nx-sort-' + m);
        });
      } catch { /* sessionStorage unavailable */ }
    }
    setLoading(true);
    setLoadError(false);

    /* M4: Build pipeline stage maps before normalizing deals */
    let _pipelineStages = [];
    try {
      const pipelineRes = await dealApi.getPipeline(id, _activePipelineId || undefined);
      const stages = pipelineRes?.stages || [];
      const sorted = [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const shortMap = {};
      const uuidMap  = {};
      sorted.forEach((stage, i) => {
        const short = 's' + (i + 1);
        shortMap[stage.id] = short;
        uuidMap[short]     = stage.id;
        _pipelineStages.push({
          id:         short,
          uuid:       stage.id,
          name:       stage.name,
          tone:       stage.color || '#94a3b8',
          prob:       stage.probability ?? 0,
          kind:       stage.kind || 'OPEN',
          slaHours:   stage.slaHours ?? null,
          pipelineId: stage.pipelineId ?? null,
        });
      });
      _stageShortMap = shortMap;
      _stageUuidMap  = uuidMap;
    } catch { /* keep existing maps */ }

    /* F-14: backend pageSize cap raised to 500; deals fetched with includeArchived:true
       so active + archived are split in one call. */
    const [peopleRes, companiesRes, dealsAllRes, tasksRes, docsRes, notesRes, svRes, membersRes, activitiesRes, resolvedBrandingRes, workspaceRes, cfDefsPersonRes, cfDefsCompanyRes, cfDefsDealRes, cfDefsTaskRes, allStagesRes] =
      await Promise.allSettled([
        peopleApi.list(id,          { pageSize: 500 }),
        companyApi.list(id,         { pageSize: 500 }),
        dealApi.list(id,            { includeArchived: true, pageSize: 500 }),
        taskApi.list(id,            { pageSize: 100 }),
        documentApi.list(id,        { pageSize: 100 }),
        noteApi.list(id,            { pageSize: 100 }),
        savedViewApi.list(id),
        workspaceApi.listMembers(id),
        activityApi.list(id,        { pageSize: 50 }),
        brandingApi.getResolvedBranding(id),
        workspaceApi.getOne(id),
        customFieldsApi.getDefinitions(id, 'people'),
        customFieldsApi.getDefinitions(id, 'companies'),
        customFieldsApi.getDefinitions(id, 'deals'),
        customFieldsApi.getDefinitions(id, 'tasks'),
        stageApi.list(id),
      ]);

    /* WS-002: discard results if workspace switched while awaiting */
    if (loadIdRef.current !== loadToken) { setLoading(false); return; }

    const extract = (r) => {
      if (r.status !== 'fulfilled') return null;
      const v = r.value;
      return Array.isArray(v) ? v : (v?.data ?? []);
    };

    /* M4: translate UUID stageId → short stageId; compute age from createdAt */
    const normDealWithStage = (d) => {
      const norm = normDeal(d);
      const msPerDay = 86400000;
      const age = norm.createdAt
        ? Math.max(0, Math.floor((Date.now() - new Date(norm.createdAt).getTime()) / msPerDay))
        : 0;
      return {
        ...norm,
        stageId:    _stageShortMap[norm.stageId] || norm.stageId,
        pipelineId: d.stage?.pipelineId || null,
        archived:   !!d.archived,
        age,
        lastTouch:  norm.lastActivity || '—',
      };
    };

    const people         = extract(peopleRes);
    const companies      = extract(companiesRes);
    const dealsAll       = extract(dealsAllRes);
    const tasks          = extract(tasksRes);
    const documents      = extract(docsRes);
    const notes          = extract(notesRes);
    const svRaw          = extract(svRes);
    const membersRaw     = extract(membersRes);
    const activitiesRaw  = extract(activitiesRes);

    /* Build workspaceMembers with userId for owner/assignee UUID resolution */
    /* FIX-01: filter by status=ACTIVE before mapping — SUSPENDED members must not
       appear in memberNames or _memberMap; missing status is treated as non-ACTIVE. */
    const workspaceMembers = membersRaw
      ? membersRaw
          .filter(m => m.status === 'ACTIVE')
          .map(m => ({ name: m.member?.name || m.name || '', userId: m.member?.userId || m.userId || null }))
          .filter(m => m.name)
      : null;
    const memberNames = workspaceMembers ? workspaceMembers.map(m => m.name) : null;

    /* LV-05: resolve current user's Member.id for view ownership checks */
    const currentUserId = window.__nx_auth?.user?.id || null;
    const currentMemberId = (membersRaw && currentUserId)
      ? (membersRaw.find(m => (m.member?.userId || m.userId) === currentUserId)?.memberId ?? null)
      : null;

    /* Populate module-level name→userId map for payload builders */
    if (workspaceMembers) {
      _memberMap = {};
      workspaceMembers.forEach(m => { if (m.userId) _memberMap[m.name] = m.userId; });
    }

    const resolvedBranding = normalizeBranding(resolvedBrandingRes.status === 'fulfilled' ? resolvedBrandingRes.value : null);
    if (resolvedBranding) {
      /* BRANDING-007: Don't overwrite branding when SA is viewing a different workspace's
         console (/workspaces/:otherId). WorkspaceConsoleLayout fetches and applies the
         URL workspace's branding independently; the session workspace's loadWorkspace
         must not stomp it. Only apply when the path matches the loaded workspace. */
      const pathMatch = typeof window !== 'undefined'
        ? window.location.pathname.match(/^\/workspaces\/([^/]+)/)
        : null;
      if (!pathMatch || pathMatch[1] === id) {
        applyBrandingGlobally(resolvedBranding);
      }
    }

    const cfDefs = {
      people:    cfDefsPersonRes.status  === 'fulfilled' ? (Array.isArray(cfDefsPersonRes.value)  ? cfDefsPersonRes.value  : []) : [],
      companies: cfDefsCompanyRes.status === 'fulfilled' ? (Array.isArray(cfDefsCompanyRes.value) ? cfDefsCompanyRes.value : []) : [],
      deals:     cfDefsDealRes.status    === 'fulfilled' ? (Array.isArray(cfDefsDealRes.value)    ? cfDefsDealRes.value    : []) : [],
      tasks:     cfDefsTaskRes.status    === 'fulfilled' ? (Array.isArray(cfDefsTaskRes.value)    ? cfDefsTaskRes.value    : []) : [],
    };

    /* CF-02: extract __default__ view column preferences from boot-loaded saved views */
    const cfColumnsFromDefaults = { people: null, companies: null, deals: null, tasks: null };
    if (svRaw) {
      svRaw.forEach(sv => {
        const normalized = savedViewApi.normView(sv);
        if (normalized.name === '__default__' && normalized.module in cfColumnsFromDefaults) {
          cfColumnsFromDefaults[normalized.module] = normalized.columns;
        }
      });
    }

    const workspaceDetails = workspaceRes.status === 'fulfilled' ? workspaceRes.value : null;
    const wsModules = Array.isArray(workspaceDetails?.modules) ? workspaceDetails.modules : [];

    const activeDeals   = dealsAll ? dealsAll.filter(d => !d.archived).map(normDealWithStage) : null;
    const archivedDeals = dealsAll ? dealsAll.filter(d =>  d.archived).map(normDealWithStage) : null;

    const allStagesRaw = allStagesRes.status === 'fulfilled' ? allStagesRes.value : null;
    const allStages    = allStagesRaw ? (Array.isArray(allStagesRaw) ? allStagesRaw : (allStagesRaw?.data || [])) : null;

    setState(s => ({
      ...s,
      pipeline:      _pipelineStages.length ? _pipelineStages                   : s.pipeline,
      allStages:     allStages              ? allStages                         : s.allStages,
      people:        people        ? people.map(normPerson)                      : s.people,
      companies:     companies     ? companies.map(normCompany)                  : s.companies,
      deals:         activeDeals   ? activeDeals                                 : s.deals,
      archivedDeals: archivedDeals ? archivedDeals                              : s.archivedDeals,
      tasks:         tasks         ? tasks.map(normTask)                         : s.tasks,
      documents:     documents     ? documents.map(normDocument)                 : s.documents,
      notes:         notes         ? notes.map(normNoteLocal)                    : s.notes,
      activity:      activitiesRaw ? activitiesRaw.map(activityApi.normActivity) : s.activity,
      savedViews:    svRaw         ? svRaw.map(savedViewApi.normView).filter(v => v.name !== '__default__') : s.savedViews,
      memberNames:      memberNames      ? memberNames      : s.memberNames,
      workspaceMembers: workspaceMembers ? workspaceMembers : s.workspaceMembers,
      currentMemberId:  currentMemberId,
      branding:         resolvedBranding                    ?? s.branding,
      modules:          wsModules,
      customFieldDefs:   cfDefs,
      cfColumns:         cfColumnsFromDefaults,
      cfColumnsDefaults: cfColumnsFromDefaults,
    }));
    setLoadError(companiesRes.status !== 'fulfilled');
    setLoading(false);
  }, []);

  /* ── React to session-changed (login, workspace switch, logout) ── */
  useEffect(() => {
    const onSessionChanged = (e) => {
      const user = e.detail?.user;
      const newWsId = user?.activeWorkspaceId || null;
      /* WS-SW-01: close any open drawers/forms when workspace changes to prevent
         stale cross-workspace record data from persisting in the new workspace.
         wsIdRef tracks the current workspace across the stale closure. */
      if (newWsId !== wsIdRef.current) {
        setDrawer(null);
        setCreateForm(null);
        setEditForm(null);
        setConfirmState(null);
      }
      setWsId(newWsId);
      pendingIds.current = {}; /* WS-003: clear stale temp→real ID mappings on workspace switch */
      if (!newWsId) {
        /* RT-02: logout — clear list-state sessionStorage so a subsequent login
           to the same workspace does not inherit the previous session's search. */
        try {
          ['people', 'companies', 'deals', 'tasks'].forEach(m => {
            sessionStorage.removeItem('nx-filters-' + m);
            sessionStorage.removeItem('nx-filters-' + m + '-search');
            sessionStorage.removeItem('nx-sort-' + m);
          });
        } catch { /* sessionStorage unavailable */ }
        prevLoadedWsIdRef.current = null;
        setState(emptyState);
      }
    };
    window.addEventListener('nx-session-changed', onSessionChanged);
    return () => window.removeEventListener('nx-session-changed', onSessionChanged);
  }, []);

  /* ── Refresh branding after workspace white-label save ───── */
  useEffect(() => {
    const onWlSaved = async (e) => {
      const id = e.detail?.wsId;
      if (!id || id !== wsId) return;
      try {
        const b = normalizeBranding(await brandingApi.getResolvedBranding(id));
        if (b) {
          applyBrandingGlobally(b);
          setState(s => ({ ...s, branding: b }));
        }
      } catch { /* silent — sidebar will reflect current values; resolves on next full load */ }
    };
    window.addEventListener('nx-wl-saved', onWlSaved);
    return () => window.removeEventListener('nx-wl-saved', onWlSaved);
  }, [wsId]);

  /* ── Keep store.modules in sync when workspace module config changes ── */
  /* Mirrors what WorkspaceRuntimeContext does on nx-modules-changed so that
     store.modules and runtimeModules never diverge after an admin module update. */
  useEffect(() => {
    const onModulesChanged = async (e) => {
      const changedWsId = e?.detail?.workspaceId;
      if (!wsId || (changedWsId && changedWsId !== wsId)) return;
      try {
        const ws = await workspaceApi.getOne(wsId);
        const modules = Array.isArray(ws?.modules) ? ws.modules : [];
        setState(s => ({ ...s, modules }));
      } catch { /* silent — stale modules resolve on next full workspace load */ }
    };
    window.addEventListener('nx-modules-changed', onModulesChanged);
    return () => window.removeEventListener('nx-modules-changed', onModulesChanged);
  }, [wsId]);

  /* ── Fetch when wsId is known ────────────────────────────── */
  useEffect(() => {
    if (wsId) loadWorkspace(wsId);
    else setState(emptyState);
  }, [wsId, loadWorkspace]);

  /* ── Helpers ─────────────────────────────────────────────── */
  const newId = (prefix) => prefix + '_' + Math.random().toString(36).slice(2, 9);

  const logActivity = useCallback((entry) => {
    const tempId = newId('a');
    setState(s => ({
      ...s,
      activity: [{ id: tempId, time: 'just now', ...entry }, ...s.activity].slice(0, 50),
    }));
    if (wsId && entry.what) {
      const { type, what, detail, personId, companyId, dealId } = entry;
      activityApi.create(wsId, {
        type:        (type || 'ACTIVITY').toUpperCase(),
        description: what,
        ...(detail    ? { detail }    : {}),
        ...(personId  ? { personId }  : {}),
        ...(companyId ? { companyId } : {}),
        ...(dealId    ? { dealId }    : {}),
      })
      .then(a => setState(s => ({
        ...s,
        activity: s.activity.map(x => x.id === tempId ? activityApi.normActivity(a) : x),
      })))
      .catch(() => {
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'Activity sync pending', desc: 'Could not persist. Will retry on reload.' });
      });
    }
  }, [wsId, toast]);

  const findDuplicate = useCallback((type, payload) => {
    const norm = (s) => (s || '').toString().trim().toLowerCase();
    if (type === 'people' && payload.email)
      return state.people.find(p => norm(p.email) === norm(payload.email));
    if (type === 'people' && payload.name)
      return state.people.find(p => norm(p.name) === norm(payload.name) && norm(p.company) === norm(payload.company));
    if (type === 'companies' && payload.name)
      return state.companies.find(c => norm(c.name) === norm(payload.name));
    if (type === 'deals' && payload.title)
      return state.deals.find(d => norm(d.title) === norm(payload.title) && d.companyId === payload.companyId);
    return null;
  }, [state]);

  /* ── create (optimistic + background backend sync) ─────────── */
  const create = useCallback((type, payload, opts = {}) => {
    if (!opts.skipDedupe) {
      const dup = findDuplicate(type, payload);
      if (dup) {
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'Duplicate Detected', desc: 'A matching ' + singular(type) + ' already exists. Opening it instead.' });
        setDrawer(dup);
        return dup.id;
      }
    }

    const tempId = newId(type[0]);
    const record = { id: tempId, ...payload };
    setState(s => ({ ...s, [type]: [record, ...s[type]] }));

    if (wsId) {
      apiCreate(type, wsId, payload)
        .then(backendRecord => {
          // FIX-06: normRecord is fallible — isolate it so normalization errors
          // cannot reach .catch() and trigger a false "create failed" toast.
          let normalized;
          try {
            normalized = normRecord(type, backendRecord);
          } catch {
            normalized = backendRecord;
          }
          setState(s => ({
            ...s,
            [type]: s[type].map(r => r.id === tempId ? { ...r, ...normalized } : r),
          }));
          pendingIds.current[tempId] = normalized.id;
          // FIX-05: success toast only after confirmed backend 201
          toast({ tone: 'success', icon: 'Check', title: titleOf(type, 'created'), desc: payload.name || payload.title || 'Record created' });
          /* Backend auto-logs DEAL_CREATED and TASK_CREATED.
             For people/companies the backend has no auto-log — persist one here. */
          if (type === 'people' || type === 'companies') {
            activityApi.create(wsId, {
              type: 'ACTIVITY',
              description: `${singular(type)} "${payload.name || ''}" created`,
              ...(type === 'people'    ? { personId:  normalized.id } : {}),
              ...(type === 'companies' ? { companyId: normalized.id } : {}),
            }).catch(() => {});
          }
        })
        .catch((err) => {
          /* F-10: rollback optimistic record on backend failure */
          setState(s => ({ ...s, [type]: s[type].filter(r => r.id !== tempId) }));
          const desc = err?.message || 'Could not save. Please try again.';
          toast({ tone: 'error', icon: 'AlertCircle', title: titleOf(type, 'create failed'), desc });
        });
    }

    return tempId;
  }, [toast, logActivity, findDuplicate, wsId]);

  /* ── update (optimistic + background sync) ─────────────────── */
  const update = useCallback((type, id, patch) => {
    /* M5: Task done toggle → POST lifecycle endpoint */
    if (type === 'tasks' && Object.keys(patch).length === 1 && 'done' in patch) {
      const prevTask = stateRef.current.tasks.find(r => r.id === id);
      setState(s => ({
        ...s,
        tasks: s.tasks.map(r => r.id === id
          ? { ...r, done: patch.done, status: patch.done ? 'done' : 'open' }
          : r),
      }));
      toast({ tone: 'success', icon: 'Check', title: patch.done ? 'Task completed' : 'Task reopened' });
      if (wsId) {
        const realId = pendingIds.current[id] || id;
        (patch.done ? taskApi.complete(wsId, realId) : taskApi.reopen(wsId, realId))
          .catch(() => {
            if (prevTask) setState(s => ({ ...s, tasks: s.tasks.map(r => r.id === id ? prevTask : r) }));
            toast({ tone: 'error', icon: 'AlertCircle', title: patch.done ? 'Complete failed' : 'Reopen failed', desc: 'Could not save. Reverted.' });
          });
      }
      return;
    }

    /* Deal archive / restore — move between deals ↔ archivedDeals */
    if (type === 'deals' && 'archived' in patch) {
      const archiving = !!patch.archived;
      const prevActiveDeal   = stateRef.current.deals.find(d => d.id === id);
      const prevArchivedDeal = stateRef.current.archivedDeals.find(d => d.id === id);
      setState(s => {
        const activeDeal   = s.deals.find(d => d.id === id);
        const archivedDeal = s.archivedDeals.find(d => d.id === id);
        return {
          ...s,
          deals: archiving
            ? s.deals.filter(d => d.id !== id)
            : (archivedDeal ? [...s.deals, { ...archivedDeal, archived: false }] : s.deals),
          archivedDeals: archiving
            ? (activeDeal ? [...s.archivedDeals, { ...activeDeal, archived: true }] : s.archivedDeals)
            : s.archivedDeals.filter(d => d.id !== id),
        };
      });
      toast({ tone: archiving ? 'warning' : 'success', icon: 'Archive', title: archiving ? 'Deal archived' : 'Deal restored' });
      if (wsId) {
        const realId = pendingIds.current[id] || id;
        (archiving ? dealApi.archive(wsId, realId) : dealApi.restore(wsId, realId))
          .then(() => {
            activityApi.create(wsId, {
              type: 'DEAL_UPDATED',
              description: archiving ? 'Deal archived' : 'Deal restored',
              dealId: realId,
            }).catch(() => {});
          })
          .catch(() => {
            setState(s => ({
              ...s,
              deals: archiving
                ? (prevActiveDeal ? [...s.deals, prevActiveDeal] : s.deals)
                : s.deals.filter(d => d.id !== id),
              archivedDeals: archiving
                ? s.archivedDeals.filter(d => d.id !== id)
                : (prevArchivedDeal ? [...s.archivedDeals, prevArchivedDeal] : s.archivedDeals),
            }));
            toast({ tone: 'error', icon: 'AlertCircle', title: archiving ? 'Archive failed' : 'Restore failed', desc: 'Could not update. Reverted.' });
          });
      }
      return;
    }

    /* Company archive — POST /:id/archive endpoint, remove from active list */
    if (type === 'companies' && patch.archived === true) {
      const prevCompany = stateRef.current.companies.find(c => c.id === id);
      setState(s => ({ ...s, companies: s.companies.filter(c => c.id !== id) }));
      toast({ tone: 'warning', icon: 'Archive', title: 'Company archived' });
      if (wsId) {
        const realId = pendingIds.current[id] || id;
        companyApi.archive(wsId, realId)
          .then(() => {
            activityApi.create(wsId, {
              type: 'ACTIVITY',
              description: 'Company archived',
              companyId: realId,
            }).catch(() => {});
          })
          .catch(() => {
            if (prevCompany) setState(s => ({ ...s, companies: [prevCompany, ...s.companies] }));
            toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: 'Could not archive. Reverted.' });
          });
      }
      return;
    }

    /* M4: Deal stage move → moveStage API (translates short ID → UUID) */
    if (type === 'deals' && 'stageId' in patch) {
      const prevDeal = stateRef.current.deals.find(r => r.id === id);
      setState(s => ({ ...s, deals: s.deals.map(r => r.id === id ? { ...r, ...patch } : r) }));
      toast({ tone: 'success', icon: 'Check', title: 'Deal moved' });
      if (wsId) {
        const realId    = pendingIds.current[id] || id;
        const stageUuid = _stageUuidMap[patch.stageId] || patch.stageId;
        dealApi.moveStage(wsId, realId, stageUuid)
          .catch(() => {
            if (prevDeal) setState(s => ({ ...s, deals: s.deals.map(r => r.id === id ? prevDeal : r) }));
            toast({ tone: 'error', icon: 'AlertCircle', title: 'Stage move failed', desc: 'Could not move. Reverted.' });
          });
        const rest = Object.fromEntries(Object.entries(patch).filter(([k]) => k !== 'stageId'));
        if (Object.keys(rest).length > 0)
          /* F-12: was silent empty catch */
          apiUpdate('deals', wsId, realId, rest).catch(() =>
            toast({ tone: 'warning', icon: 'AlertCircle', title: 'Partial sync pending', desc: 'Some deal fields saved locally.' }),
          );
      }
      return;
    }

    setState(s => {
      const next = { ...s };
      next[type] = s[type].map(r => r.id === id ? { ...r, ...patch } : r);
      if (type === 'companies') {
        const old = s.companies.find(c => c.id === id);
        if (old && patch.name && patch.name !== old.name) {
          next.people = s.people.map(p => p.companyId === id ? { ...p, company: patch.name } : p);
          next.conversations = s.conversations.map(c => {
            const person = s.people.find(p => p.name === c.name);
            return person && person.companyId === id ? { ...c, company: patch.name } : c;
          });
        }
      }
      if (type === 'people') {
        const old = s.people.find(p => p.id === id);
        if (old && patch.name && patch.name !== old.name)
          next.conversations = s.conversations.map(c => c.name === old.name ? { ...c, name: patch.name } : c);
      }
      return next;
    });
    /* F-10: capture pre-update record for rollback before optimistic setState runs */
    const prevRecord = stateRef.current[type]?.find(r => r.id === id);
    toast({ tone: 'success', icon: 'Check', title: titleOf(type, 'updated'), desc: 'Changes saved' });

    if (wsId) {
      const realId = pendingIds.current[id] || id;
      apiUpdate(type, wsId, realId, patch)
        .then(backendRecord => {
          if (backendRecord?.id) {
            const normalized = normRecord(type, backendRecord);
            setState(s => ({ ...s, [type]: s[type].map(r => r.id === id ? { ...r, ...normalized } : r) }));
          }
        })
        .catch(() => {
          if (prevRecord) {
            setState(s => ({ ...s, [type]: s[type].map(r => r.id === id ? prevRecord : r) }));
          }
          toast({ tone: 'error', icon: 'AlertCircle', title: titleOf(type, 'update failed'), desc: 'Could not save. Reverted.' });
        });
    }
  }, [toast, wsId]);

  /* ── remove (optimistic + background sync) ─────────────────── */
  const remove = useCallback((type, id) => {
    /* F-10: capture record before removal for rollback */
    const prevRecord = stateRef.current[type]?.find(r => r.id === id);

    setState(s => {
      const rec = s[type].find(r => r.id === id);
      const next = { ...s, [type]: s[type].filter(r => r.id !== id) };
      if (type === 'people') {
        next.deals = s.deals.filter(d => d.contactId !== id);
        next.conversations = rec ? s.conversations.filter(c => c.name !== rec.name) : s.conversations;
      }
      if (type === 'companies') {
        next.deals = s.deals.filter(d => d.companyId !== id);
        next.people = s.people.map(p => p.companyId === id ? { ...p, companyId: null, company: '—' } : p);
      }
      return next;
    });
    if (drawerIdRef.current === id) setDrawer(null);
    toast({ tone: 'error', icon: 'Trash', title: titleOf(type, 'deleted'), desc: '1 record removed' });

    if (wsId) {
      const realId = pendingIds.current[id] || id;
      apiRemove(type, wsId, realId).catch(() => {
        if (prevRecord) {
          setState(s => ({ ...s, [type]: [prevRecord, ...s[type]] }));
        }
        toast({ tone: 'error', icon: 'AlertCircle', title: titleOf(type, 'delete failed'), desc: 'Could not delete. Record restored.' });
      });
    }
  }, [toast, wsId]);

  /* ── bulk ops (optimistic + background sync) ────────────────── */
  const bulkRemove = useCallback((type, ids) => {
    const prevRecords = stateRef.current[type]?.filter(r => ids.includes(r.id)) || [];
    setState(s => {
      const set = new Set(ids);
      const next = { ...s, [type]: s[type].filter(r => !set.has(r.id)) };
      if (type === 'people') {
        const removedNames = new Set(s.people.filter(p => set.has(p.id)).map(p => p.name));
        next.deals = s.deals.filter(d => !set.has(d.contactId));
        next.conversations = s.conversations.filter(c => !removedNames.has(c.name));
      }
      if (type === 'companies') {
        next.deals = s.deals.filter(d => !set.has(d.companyId));
        next.people = s.people.map(p => set.has(p.companyId) ? { ...p, companyId: null, company: '—' } : p);
      }
      return next;
    });
    toast({ tone: 'error', icon: 'Trash', title: ids.length + ' ' + (ids.length === 1 ? singular(type) : type) + ' deleted' });
    if (wsId) {
      Promise.allSettled(ids.map(id => apiRemove(type, wsId, pendingIds.current[id] || id)))
        .then(results => {
          const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
          if (failedIds.length > 0) {
            const toRestore = prevRecords.filter(r => failedIds.includes(r.id));
            if (toRestore.length > 0)
              setState(s => ({ ...s, [type]: [...toRestore, ...s[type]] }));
            toast({ tone: 'error', icon: 'AlertCircle', title: failedIds.length + ' delete(s) failed', desc: 'Some records could not be removed.' });
          }
        });
    }
  }, [toast, wsId]);

  const bulkUpdate = useCallback((type, ids, patch) => {
    const prevRecords = stateRef.current[type]?.filter(r => ids.includes(r.id)) || [];
    setState(s => {
      const set = new Set(ids);
      return { ...s, [type]: s[type].map(r => set.has(r.id) ? { ...r, ...patch } : r) };
    });
    toast({ tone: 'success', icon: 'Check', title: ids.length + ' updated', desc: Object.keys(patch).join(', ') + ' applied' });
    if (wsId) {
      Promise.allSettled(ids.map(id => apiUpdate(type, wsId, pendingIds.current[id] || id, patch)))
        .then(results => {
          const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
          if (failedIds.length > 0) {
            const toRevert = prevRecords.filter(r => failedIds.includes(r.id));
            if (toRevert.length > 0)
              setState(s => ({ ...s, [type]: s[type].map(r => toRevert.find(p => p.id === r.id) || r) }));
            toast({ tone: 'error', icon: 'AlertCircle', title: failedIds.length + ' update(s) failed', desc: 'Some records reverted.' });
          }
        });
    }
  }, [toast, wsId]);

  /* ── Drawer / form / confirm helpers ────────────────────────── */
  const openDrawer      = useCallback((record) => {
    setDrawer(record);
    if (record) {
      try {
        const prev = JSON.parse(sessionStorage.getItem('nx-recent-records') || '[]');
        const type = 'done' in record ? 'task' : record.stageId !== undefined ? 'deal' : record.domain !== undefined ? 'company' : 'person';
        const next = [{ id: record.id, type }, ...prev.filter(r => r.id !== record.id)].slice(0, 8);
        sessionStorage.setItem('nx-recent-records', JSON.stringify(next));
      } catch (_) {}
    }
  }, []);
  const closeDrawer     = useCallback(() => setDrawer(null),                                  []);
  const openCreateForm  = useCallback((type, prefill = {}) => setCreateForm({ type, prefill }), []);
  const closeCreateForm = useCallback(() => setCreateForm(null),                              []);
  const openEditForm    = useCallback((type, record) => setEditForm({ type, record }),        []);
  const closeEditForm   = useCallback(() => setEditForm(null),                                []);
  const confirm         = useCallback((cfg) => setConfirmState(cfg),                          []);
  const closeConfirm    = useCallback(() => setConfirmState(null),                            []);

  /* ── Saved views (M8: backend-persisted) ────────────────────── */
  // CF-06 PART B: columns added; PHASE-A: sort added so named SavedViews persist sort state.
  const saveView = useCallback((module, name, filters, search, columns, sort) => {
    const view = { id: newId('sv'), module, name, filters: filters || {}, search: search || '', columns: columns ?? null, sort: sort ?? null };
    setState(s => ({ ...s, savedViews: [...s.savedViews, view] }));
    toast({ tone: 'success', icon: 'Star', title: 'View saved', desc: name });
    if (wsId) {
      savedViewApi.create(wsId, { name, module, filters, search, columns: columns ?? undefined, sort: sort ?? undefined })
        .then(sv => {
          const normalized = savedViewApi.normView(sv);
          setState(s => ({
            ...s,
            savedViews: s.savedViews.map(v => v.id === view.id ? normalized : v),
          }));
          pendingIds.current[view.id] = normalized.id;
        })
        .catch(() => {
          /* F-11: rollback optimistic view on backend failure */
          setState(s => ({ ...s, savedViews: s.savedViews.filter(v => v.id !== view.id) }));
          toast({ tone: 'warning', icon: 'AlertCircle', title: 'View save failed', desc: 'Could not save view. Please try again.' });
        });
    }
    return view.id;
  }, [toast, wsId]);

  const removeView = useCallback((id) => {
    const prevView = stateRef.current.savedViews.find(v => v.id === id);
    setState(s => ({ ...s, savedViews: s.savedViews.filter(v => v.id !== id) }));
    toast({ tone: 'error', icon: 'Trash', title: 'View deleted' });
    if (wsId) {
      const realId = pendingIds.current[id] || id;
      savedViewApi.remove(wsId, realId).catch(() => {
        /* F-11: restore view if backend delete failed */
        if (prevView) setState(s => ({ ...s, savedViews: [prevView, ...s.savedViews] }));
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'View delete failed', desc: 'Could not delete. View restored.' });
      });
    }
  }, [toast, wsId]);

  // LV-04: optimistic update — no upsertDefault (INVARIANT-01). Used for Rename and Overwrite.
  const updateView = useCallback((viewId, changes) => {
    if (!wsId) return;
    const prevView = stateRef.current.savedViews.find(v => v.id === viewId);
    if (!prevView) return;
    setState(s => ({ ...s, savedViews: s.savedViews.map(v => v.id === viewId ? { ...v, ...changes } : v) }));
    const realId = pendingIds.current[viewId] || viewId;
    savedViewApi.update(wsId, realId, changes)
      .then(updated => {
        const normalized = savedViewApi.normView(updated);
        setState(s => ({
          ...s,
          savedViews: s.savedViews.map(v => v.id === viewId ? normalized : v),
        }));
      })
      .catch(() => {
        if (prevView) setState(s => ({ ...s, savedViews: s.savedViews.map(v => v.id === viewId ? prevView : v) }));
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'View update failed', desc: 'Could not update view. Please try again.' });
      });
  }, [wsId, toast]);

  /* ── CF-02: Custom Field Values + Column Visibility ─────── */
  const loadCfValues = useCallback((entity, ids) => {
    if (!wsId || !ids || ids.length === 0) return;
    // MF-02: exclude optimistic temp IDs (e.g. "d_q7m2ub8") — backend @IsUUID rejects non-UUIDs.
    const UUID4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const validIds = ids.filter(id => UUID4_RE.test(id));
    if (validIds.length === 0) return;
    const BATCH = 200;
    const batches = [];
    for (let i = 0; i < validIds.length; i += BATCH) batches.push(validIds.slice(i, i + BATCH));
    // Sequential batches: each batch is bounded to 200 IDs; partial failures do not discard successful batches
    const fetchAll = async () => {
      const merged = {};
      for (const batch of batches) {
        try {
          const res = await customFieldsApi.getBatchValues(wsId, entity, batch);
          if (res?.values) Object.assign(merged, res.values);
        } catch {
          /* batch failed — continue; this batch's values degrade to '—' */
        }
      }
      setState(s => ({
        ...s,
        cfValues: {
          ...s.cfValues,
          [entity]: { ...s.cfValues[entity], ...merged },
        },
      }));
    };
    fetchAll();
  }, [wsId]);

  const setCfColumns = useCallback((entity, columns) => {
    setState(s => ({
      ...s,
      cfColumns:         { ...s.cfColumns,         [entity]: columns },
      cfColumnsDefaults: { ...s.cfColumnsDefaults, [entity]: columns },
    }));
    if (wsId) {
      savedViewApi.upsertDefault(wsId, entity, columns).catch(() => { /* non-fatal */ });
    }
  }, [wsId]);

  // LV-03: in-memory only — does NOT call upsertDefault (INVARIANT-01)
  // F-03: does NOT update cfColumnsDefaults — view apply is transient
  const setColumnsFromView = useCallback((entity, columns) => {
    setState(s => ({
      ...s,
      cfColumns: { ...s.cfColumns, [entity]: columns },
    }));
  }, []);

  // F-03: restore displayed columns to the current persisted default for this entity.
  // Local state only — no API call, no upsertDefault, no Saved View mutation.
  const resetEntityColumns = useCallback((entity) => {
    setState(s => ({
      ...s,
      cfColumns: { ...s.cfColumns, [entity]: s.cfColumnsDefaults?.[entity] ?? null },
    }));
  }, []);

  /* ── Notes (M3: backend-persisted) ─────────────────────────── */
  const createNote = useCallback(({ body, title, personId, companyId, dealId, linkTo } = {}) => {
    const tempNote = {
      id:        newId('n'),
      type:      'note',
      who:       window.__nx_auth?.user?.name || 'You',
      what:      'added a note' + (linkTo ? ' on ' + linkTo : ''),
      detail:    body      || '',
      noteTitle: title     || '',
      time:      'just now',
      personId:  personId  || null,
      companyId: companyId || null,
      dealId:    dealId    || null,
    };
    setState(s => ({ ...s, notes: [tempNote, ...s.notes] }));
    if (wsId) {
      noteApi.create(wsId, { body, title, personId, companyId, dealId })
        .then(n => {
          setState(s => ({
            ...s,
            notes: s.notes.map(r => r.id === tempNote.id ? normNoteLocal(n) : r),
          }));
          pendingIds.current[tempNote.id] = n.id;
          toast({ tone: 'success', icon: 'FileText', title: 'Note saved' });
        })
        .catch(() => {
          /* F-11: rollback optimistic note on backend failure */
          setState(s => ({ ...s, notes: s.notes.filter(n => n.id !== tempNote.id) }));
          toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note save failed', desc: 'Could not save note. Please try again.' });
        });
    }
    return tempNote.id;
  }, [toast, wsId]);

  const updateNote = useCallback((id, { body, title } = {}) => {
    const prevNote = stateRef.current.notes.find(n => n.id === id);
    setState(s => ({
      ...s,
      notes: s.notes.map(n =>
        n.id === id
          ? { ...n, detail: body ?? n.detail, noteTitle: title ?? n.noteTitle }
          : n,
      ),
    }));
    if (wsId) {
      const realId = pendingIds.current[id] || id;
      noteApi.update(wsId, realId, { body, title })
        .then(() => toast({ tone: 'success', icon: 'FileText', title: 'Note updated' }))
        .catch(() => {
        /* F-11: rollback note update on backend failure */
        if (prevNote) setState(s => ({ ...s, notes: s.notes.map(n => n.id === id ? prevNote : n) }));
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note update failed', desc: 'Could not save changes.' });
      });
    }
  }, [toast, wsId]);

  const removeNote = useCallback((id) => {
    const prevNote = stateRef.current.notes.find(n => n.id === id);
    setState(s => ({ ...s, notes: s.notes.filter(n => n.id !== id) }));
    toast({ tone: 'success', icon: 'Trash', title: 'Note deleted' });
    if (wsId) {
      const realId = pendingIds.current[id] || id;
      noteApi.remove(wsId, realId).catch(() => {
        /* F-11: restore note if backend delete failed */
        if (prevNote) setState(s => ({ ...s, notes: [prevNote, ...s.notes] }));
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note delete failed', desc: 'Could not delete. Note restored.' });
      });
    }
  }, [toast, wsId]);

  /* ── Task lifecycle actions ─────────────────────────────────── */
  const startTask = useCallback((id) => {
    const prevTask = stateRef.current.tasks.find(t => t.id === id);
    setState(s => ({
      ...s,
      tasks: s.tasks.map(t => t.id === id ? { ...t, done: false, status: 'in_progress' } : t),
    }));
    toast({ tone: 'success', icon: 'Play', title: 'Task started' });
    if (wsId) {
      const realId = pendingIds.current[id] || id;
      taskApi.start(wsId, realId)
        .catch(() => {
          if (prevTask) setState(s => ({ ...s, tasks: s.tasks.map(t => t.id === id ? prevTask : t) }));
          toast({ tone: 'error', icon: 'AlertCircle', title: 'Start failed', desc: 'Could not start. Reverted.' });
        });
    }
  }, [toast, wsId]);

  const cancelTask = useCallback((id) => {
    const prevTask = stateRef.current.tasks.find(t => t.id === id);
    setState(s => ({
      ...s,
      tasks: s.tasks.map(t => t.id === id ? { ...t, status: 'cancelled', done: false } : t),
    }));
    toast({ tone: 'warning', icon: 'XCircle', title: 'Task cancelled' });
    if (wsId) {
      const realId = pendingIds.current[id] || id;
      taskApi.cancel(wsId, realId)
        .catch(() => {
          if (prevTask) setState(s => ({ ...s, tasks: s.tasks.map(t => t.id === id ? prevTask : t) }));
          toast({ tone: 'error', icon: 'AlertCircle', title: 'Cancel failed', desc: 'Could not cancel. Reverted.' });
        });
    }
  }, [toast, wsId]);

  const archiveTask = useCallback((id) => {
    const task = stateRef.current.tasks.find(t => t.id === id);
    setState(s => ({
      ...s,
      tasks:         s.tasks.filter(t => t.id !== id),
      archivedTasks: task ? [...s.archivedTasks, { ...task, archived: true }] : s.archivedTasks,
    }));
    toast({ tone: 'warning', icon: 'Archive', title: 'Task archived' });
    if (wsId) {
      const realId = pendingIds.current[id] || id;
      taskApi.archive(wsId, realId)
        .catch(() => {
          if (task) setState(s => ({
            ...s,
            tasks:         [task, ...s.tasks],
            archivedTasks: s.archivedTasks.filter(t => t.id !== id),
          }));
          toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: 'Could not archive. Reverted.' });
        });
    }
  }, [toast, wsId]);

  /* ── Live drawer (keeps detail in sync with list) ───────────── */
  const liveDrawer = useMemo(() => {
    if (!drawer) return null;
    if (drawer.role    !== undefined) return state.people.find(p => p.id === drawer.id)    || drawer;
    if (drawer.domain  !== undefined) return state.companies.find(c => c.id === drawer.id) || drawer;
    if (drawer.stageId !== undefined) return state.deals.find(d => d.id === drawer.id) || state.archivedDeals.find(d => d.id === drawer.id) || drawer;
    if ('done' in drawer)             return state.tasks.find(t => t.id === drawer.id) || (state.archivedTasks || []).find(t => t.id === drawer.id) || drawer;
    return drawer;
  }, [drawer, state.people, state.companies, state.deals, state.archivedDeals, state.tasks, state.archivedTasks]);

  const value = useMemo(() => ({
    ...state,
    loading,
    loadError,
    workspaceId: wsId,
    drawer: liveDrawer,
    createForm,
    editForm,
    confirmState,
    create, update, remove, bulkRemove, bulkUpdate,
    startTask, cancelTask, archiveTask,
    openDrawer, closeDrawer,
    openCreateForm, closeCreateForm,
    openEditForm,   closeEditForm,
    confirm, closeConfirm,
    saveView, removeView, updateView,
    logActivity,
    createNote, updateNote, removeNote,
    loadCfValues, setCfColumns, setColumnsFromView, resetEntityColumns,
    activePipelineId: _activePipelineId,
    pipelines: state.pipelines,
    setPipelines: (list) => setState(s => ({ ...s, pipelines: Array.isArray(list) ? list : [] })),
    setActivePipeline: (pipelineId) => { _activePipelineId = pipelineId ?? null; if (wsId) loadWorkspace(wsId); },
    reloadWorkspace: () => wsId && loadWorkspace(wsId),
    resetData: () => {
      setState(emptyState());
      if (wsId) loadWorkspace(wsId);
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Data reloaded from server' });
    },
  }), [state, loading, loadError, wsId, liveDrawer, createForm, editForm, confirmState,
    create, update, remove, bulkRemove, bulkUpdate,
    startTask, cancelTask, archiveTask,
    openDrawer, closeDrawer, openCreateForm, closeCreateForm,
    openEditForm, closeEditForm, confirm, closeConfirm,
    saveView, removeView, updateView, logActivity, loadWorkspace, toast,
    createNote, updateNote, removeNote, loadCfValues, setCfColumns, setColumnsFromView, resetEntityColumns]);

  if (typeof window !== 'undefined') window.__nx_store = value;

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error('useStore must be used inside StoreProvider');
  return ctx;
};
